import {
	Button,
	Listbox,
	ListboxButton,
	ListboxOption,
	ListboxOptions,
	ListboxProps,
} from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/24/solid';
import clsx from 'clsx';
import { Fragment } from 'react';

import { Field } from './field';
import { Hint } from './hint';
import { Label } from './label';

export type SelectProps = ListboxProps & {
	'aria-label'?: string;
	label?: string;
	hint?: string;
	size?: 'sm' | 'md' | 'lg' | '36' | '44';
	options: Partial<{
		label: string;
		value: string;
		hint?: string;
		color?: string;
	}>[];
	action?: React.ReactNode;
};

export const Select = ({
	label,
	hint,
	size,
	options,
	action,
	'aria-label': ariaLabel,
	...props
}: SelectProps) => {
	const className = clsx(
		{
			// sizes
			'h-12 px-4 py-3 text-base': !size || size === 'md',
			'h-10 px-4 py-3 text-sm': size === 'sm',
			'h-14 px-6 py-3 text-lg': size === 'lg',
			'h-9 px-4 py-2 text-sm': size === '36',
			'h-11 px-4 py-2 text-sm': size === '44',
			// states
			'cursor-not-allowed opacity-50': props.disabled,
			// 'cursor-not-allowed': props.readOnly,
			'ring-rose-500': props.invalid,
		},
		'flex w-full items-center justify-between gap-1 text-left',
		// 'transition-all',
		'rounded-lg border border-gray-4 bg-white text-black',
		'data-[hover]:border-gray-3',
		'data-[focus]:outline data-[focus]:outline-2 data-[focus]:outline-offset-2 data-[focus]:outline-accent',
		'data-[active]:border-gray-3 data-[active]:shadow-[inset_0_0_0_1px_theme(colors.gray.3)]',
		'[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
	);

	const selectedLabel = (value: string) =>
		options.find((option) => option.value === value)?.label || '';

	return (
		<Field>
			{label && (
				<div className="mb-2">
					<Label action={action}>{label}</Label>
				</div>
			)}
			<Listbox {...props}>
				<ListboxButton as={Fragment}>
					{({ value }) => {
						const selectedOption = options.find(
							(option) => option.value === value,
						);
						return (
							<Button
								type="button"
								className={className}
								aria-label={ariaLabel}
							>
								<input type="hidden" name={props.name} value={value} />
								<div className="flex items-center gap-2">
									{selectedOption?.color && (
										<div
											className="size-3 rounded-full"
											style={{ backgroundColor: selectedOption.color }}
										/>
									)}
									<span>{selectedLabel(value)}</span>
								</div>
								<span className="flex-1" />
								<ChevronDownIcon className="size-5 opacity-50" />
							</Button>
						);
					}}
				</ListboxButton>
				<ListboxOptions
					anchor={{ to: 'bottom start' }}
					className="z-50 w-[var(--button-width)] min-w-36 rounded-lg bg-white p-4 shadow-listbox ring-1 ring-inset ring-black/5"
				>
					{options.map((option) => (
						<ListboxOption
							key={option.value}
							value={option.value}
							className="group w-full cursor-pointer rounded-lg px-3 py-2 text-sm data-[focus]:bg-gray-5 data-[selected]:bg-gray-5"
						>
							<div className="flex items-center gap-2">
								{option.color && (
									<div
										className="size-3 rounded-full"
										style={{ backgroundColor: option.color }}
									/>
								)}
								<span>{option.label}</span>
							</div>
							{option.hint && (
								<span className="block text-xs text-gray-3">{option.hint}</span>
							)}
						</ListboxOption>
					))}
				</ListboxOptions>
			</Listbox>
			{hint && <Hint>{hint}</Hint>}
		</Field>
	);
};
